// Draft of the project's general handover checks in "הגדרות מסירות" (H-Next): the stored checks
// plus the user's edits, saved together. Checks are deactivated, never deleted.
import {
  GENERAL_CHECK_LIMITS,
  type GeneralChecksResponse,
  type GeneralChecksSaveRequest,
} from '@sivan/shared';

export interface CheckRow {
  /** Stable key in the editor; equals `id` for stored checks. */
  readonly key: string;
  readonly id: string | null;
  readonly name: string;
  /** The unit of the quantity counted; empty: no quantity. */
  readonly unit: string;
  readonly active: boolean;
}

export type CheckRowErrors = Partial<Record<'name' | 'unit', string>>;

let counter = 0;
const newKey = (): string => {
  counter += 1;
  return `new-${String(counter)}`;
};

export function checkRowsFrom(list: GeneralChecksResponse): CheckRow[] {
  return list.items.map((item) => ({
    key: item.id,
    id: item.id,
    name: item.name,
    unit: item.quantityUnit ?? '',
    active: item.active,
  }));
}

export function toChecksRequest(
  rows: readonly CheckRow[],
  listVersion: string,
): GeneralChecksSaveRequest {
  return {
    listVersion,
    items: rows.map((row) => ({
      id: row.id,
      name: row.name.trim(),
      quantityUnit: row.unit.trim() === '' ? null : row.unit.trim(),
      active: row.active,
    })),
  };
}

export const checksDirty = (rows: readonly CheckRow[], list: GeneralChecksResponse): boolean =>
  JSON.stringify(toChecksRequest(rows, list.listVersion)) !==
  JSON.stringify(toChecksRequest(checkRowsFrom(list), list.listVersion));

/** Errors per row key; empty when the list can be saved. */
export function validateCheckRows(rows: readonly CheckRow[]): Record<string, CheckRowErrors> {
  const errors: Record<string, CheckRowErrors> = {};
  const seen = new Set<string>();
  for (const row of rows) {
    const e: CheckRowErrors = {};
    const name = row.name.trim();
    if (name === '') e.name = 'שדה חובה';
    else if (name.length > GENERAL_CHECK_LIMITS.name)
      e.name = `עד ${String(GENERAL_CHECK_LIMITS.name)} תווים`;
    else if (seen.has(name.toLowerCase())) e.name = 'הבדיקה כבר קיימת';
    seen.add(name.toLowerCase());
    if (row.unit.trim().length > GENERAL_CHECK_LIMITS.unit)
      e.unit = `עד ${String(GENERAL_CHECK_LIMITS.unit)} תווים`;
    if (Object.keys(e).length > 0) errors[row.key] = e;
  }
  return errors;
}

export function addCheckRow(
  rows: readonly CheckRow[],
  name: string,
  unit: string,
): { rows: CheckRow[]; key: string } {
  const row: CheckRow = { key: newKey(), id: null, name, unit, active: true };
  return { rows: [...rows, row], key: row.key };
}

/** Moves a check up (-1) or down (+1). */
export function moveCheckRow(rows: readonly CheckRow[], key: string, delta: -1 | 1): CheckRow[] {
  const index = rows.findIndex((r) => r.key === key);
  const target = index + delta;
  const row = rows[index];
  const other = rows[target];
  if (row === undefined || other === undefined) return [...rows];
  const next = [...rows];
  next[index] = other;
  next[target] = row;
  return next;
}
